import { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/layout/Header';
import ManualSelectionsBanner from '../components/ManualSelectionsBanner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { useAuth } from '../contexts/AuthContext';
import {
  Loader2,
  Calendar,
  ListTodo,
  MailOpen,
  ArrowRight,
  ClipboardCheck,
  FileText,
  Users,
  Clock,
  AlertTriangle,
  RefreshCw,
  ChevronRight,
  Timer,
  Eye,
  UserX,
  Bell,
  TrendingUp,
  Send,
  FileCheck,
  FileClock,
} from 'lucide-react';
import { format, parseISO, formatDistanceToNow } from 'date-fns';
import { toast } from 'sonner';

const formatDateTimeIST = (dateStr) => {
  if (!dateStr) return '-';
  try {
    const date = parseISO(dateStr);
    return format(date, 'dd MMM, h:mm a');
  } catch {
    return '-';
  }
};

const formatRelativeTime = (dateStr) => {
  if (!dateStr) return '-';
  try {
    return formatDistanceToNow(parseISO(dateStr), { addSuffix: true });
  } catch {
    return '-';
  }
};

export default function ManualSelectionsPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scope, setScope] = useState('mine');
  const [lastRefresh, setLastRefresh] = useState(null);
  const refreshTimerRef = useRef(null);

  const loadOverview = useCallback(async (showLoading = true) => {
    try {
      if (showLoading) setLoading(true);
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('ats_token');
      
      const response = await fetch(`${API_URL}/api/manual-selections/overview?scope=${scope}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setOverview(data);
        setLastRefresh(new Date());
      }
    } catch (error) {
      console.error('Failed to load overview:', error);
      toast.error('Failed to load overview');
    } finally {
      setLoading(false);
    }
  }, [scope]);

  // Initial load and scope change
  useEffect(() => {
    loadOverview();
  }, [loadOverview]);

  // Auto-refresh every 60 seconds
  useEffect(() => {
    refreshTimerRef.current = setInterval(() => {
      loadOverview(false); // Silent refresh
    }, 60000);

    return () => {
      if (refreshTimerRef.current) {
        clearInterval(refreshTimerRef.current);
      }
    };
  }, [loadOverview]);

  const handleManualRefresh = () => {
    loadOverview(true);
    toast.success('Dashboard refreshed');
  };

  const totalActionItems = overview?.total_action_items || 0;

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-gradient-to-br from-slate-700 to-slate-900 rounded-xl flex items-center justify-center shadow-lg">
              <ClipboardCheck className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900" data-testid="manual-selections-title">
                Manual Selections
              </h1>
              <p className="text-slate-500 mt-1">
                Overview of items requiring recruiter action
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            {/* Scope Toggle (Admin only) */}
            {isAdmin && (
              <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-lg border border-slate-200">
                <Label htmlFor="scope-toggle" className={`text-sm ${scope === 'mine' ? 'text-slate-900 font-medium' : 'text-slate-500'}`}>
                  Mine
                </Label>
                <Switch
                  id="scope-toggle"
                  checked={scope === 'all'}
                  onCheckedChange={(checked) => setScope(checked ? 'all' : 'mine')}
                  data-testid="scope-toggle"
                />
                <Label htmlFor="scope-toggle" className={`text-sm ${scope === 'all' ? 'text-slate-900 font-medium' : 'text-slate-500'}`}>
                  All
                </Label>
              </div>
            )}
            
            {/* Refresh Button */}
            <Button
              variant="outline"
              size="sm"
              onClick={handleManualRefresh}
              disabled={loading}
              data-testid="refresh-btn"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>

        {/* Action Required Banner */}
        {!loading && totalActionItems > 0 && (
          <div className="mb-6 p-4 bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl flex items-center gap-4">
            <div className="w-12 h-12 bg-amber-500 rounded-full flex items-center justify-center flex-shrink-0">
              <AlertTriangle className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1">
              <h2 className="text-lg font-semibold text-amber-800">
                {totalActionItems} {totalActionItems === 1 ? 'item needs' : 'items need'} your action
              </h2>
              <p className="text-sm text-amber-600 mt-0.5">
                Overdue tasks, upcoming interviews, awaiting client replies, and dossiers ready to process
              </p>
            </div>
            {lastRefresh && (
              <div className="text-xs text-amber-600 text-right">
                <p>Auto-refreshes every 60s</p>
                <p>Last: {format(lastRefresh, 'h:mm:ss a')}</p>
              </div>
            )}
          </div>
        )}

        {/* Banner */}
        <ManualSelectionsBanner />

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-slate-600" />
          </div>
        ) : overview ? (
          <div className="space-y-8">
            {/* Stats Cards Grid */}
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {/* Dossiers Card */}
              <Card className="bg-white border-indigo-200 hover:shadow-lg transition-all" data-testid="dossiers-card">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg flex items-center justify-center">
                        <FileText className="w-5 h-5 text-white" />
                      </div>
                      <CardTitle className="text-lg">Dossiers</CardTitle>
                    </div>
                    <Link to="/ats/manual-selections/dossiers">
                      <Button variant="ghost" size="sm" className="text-indigo-600">
                        View <ChevronRight className="w-4 h-4 ml-1" />
                      </Button>
                    </Link>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-3 gap-2 mb-4">
                    <div className="text-center p-2 bg-slate-50 rounded-lg">
                      <p className="text-xl font-bold text-slate-700">{overview.dossiers.counts.uploaded_not_parsed}</p>
                      <p className="text-xs text-slate-500">Uploaded</p>
                    </div>
                    <div className="text-center p-2 bg-slate-50 rounded-lg">
                      <p className="text-xl font-bold text-slate-700">{overview.dossiers.counts.parsed_not_generated}</p>
                      <p className="text-xs text-slate-500">Parsed</p>
                    </div>
                    <div className="text-center p-2 bg-amber-50 rounded-lg">
                      <p className="text-xl font-bold text-amber-600">{overview.dossiers.counts.generated_not_sent}</p>
                      <p className="text-xs text-slate-500">Ready</p>
                    </div>
                  </div>
                  {overview.dossiers.top.length > 0 ? (
                    <div className="space-y-2">
                      <p className="text-xs font-medium text-slate-500 uppercase">Needing Action</p>
                      {overview.dossiers.top.map((d) => (
                        <Link key={d.id} to={`/ats/manual-selections/dossiers`} className="block">
                          <div className="flex items-center justify-between p-2 hover:bg-slate-50 rounded-lg transition-colors">
                            <div className="flex items-center gap-2 min-w-0">
                              {d.status === 'uploaded' && <FileClock className="w-4 h-4 text-slate-400 flex-shrink-0" />}
                              {d.status === 'parsed' && <FileCheck className="w-4 h-4 text-blue-500 flex-shrink-0" />}
                              {d.status === 'generated' && <Send className="w-4 h-4 text-green-500 flex-shrink-0" />}
                              <span className="text-sm text-slate-700 truncate">{d.candidate_name || d.resume_number}</span>
                            </div>
                            <Badge variant="outline" className="text-xs flex-shrink-0">{d.status}</Badge>
                          </div>
                        </Link>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-slate-400 text-center py-4">No dossiers pending</p>
                  )}
                </CardContent>
              </Card>

              {/* Interviews Card */}
              <Card className="bg-white border-blue-200 hover:shadow-lg transition-all" data-testid="interviews-card">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center">
                        <Calendar className="w-5 h-5 text-white" />
                      </div>
                      <CardTitle className="text-lg">Interviews</CardTitle>
                    </div>
                    <Link to="/ats/manual-selections/interviews">
                      <Button variant="ghost" size="sm" className="text-blue-600">
                        View <ChevronRight className="w-4 h-4 ml-1" />
                      </Button>
                    </Link>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-2 mb-4">
                    <div className="text-center p-2 bg-blue-50 rounded-lg">
                      <p className="text-xl font-bold text-blue-600">{overview.interviews.counts.today}</p>
                      <p className="text-xs text-slate-500">Today</p>
                    </div>
                    <div className="text-center p-2 bg-amber-50 rounded-lg">
                      <p className="text-xl font-bold text-amber-600">{overview.interviews.counts.next_24h}</p>
                      <p className="text-xs text-slate-500">Next 24h</p>
                    </div>
                    <div className="text-center p-2 bg-slate-50 rounded-lg">
                      <p className="text-xl font-bold text-slate-700">{overview.interviews.counts.next_7d}</p>
                      <p className="text-xs text-slate-500">This Week</p>
                    </div>
                    <div className="text-center p-2 bg-red-50 rounded-lg">
                      <p className="text-xl font-bold text-red-600">{overview.interviews.counts.no_show_last_7d}</p>
                      <p className="text-xs text-slate-500">No-shows</p>
                    </div>
                  </div>
                  {overview.interviews.upcoming.length > 0 ? (
                    <div className="space-y-2">
                      <p className="text-xs font-medium text-slate-500 uppercase">Upcoming</p>
                      {overview.interviews.upcoming.map((i) => (
                        <Link key={i.id} to={`/ats/manual-selections/interviews`} className="block">
                          <div className="flex items-center justify-between p-2 hover:bg-slate-50 rounded-lg transition-colors">
                            <div className="min-w-0 flex-1">
                              <p className="text-sm text-slate-700 truncate">{i.candidate_name}</p>
                              <p className="text-xs text-slate-500">{i.round_name} • {i.mode}</p>
                            </div>
                            <span className="text-xs text-blue-600 flex-shrink-0 ml-2">{formatDateTimeIST(i.scheduled_at)}</span>
                          </div>
                        </Link>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-slate-400 text-center py-4">No upcoming interviews</p>
                  )}
                </CardContent>
              </Card>

              {/* Tasks Card */}
              <Card className="bg-white border-amber-200 hover:shadow-lg transition-all" data-testid="tasks-card">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-10 h-10 bg-gradient-to-br from-amber-500 to-orange-600 rounded-lg flex items-center justify-center">
                        <ListTodo className="w-5 h-5 text-white" />
                      </div>
                      <CardTitle className="text-lg">Tasks</CardTitle>
                    </div>
                    <Link to="/ats/manual-selections/tasks">
                      <Button variant="ghost" size="sm" className="text-amber-600">
                        View <ChevronRight className="w-4 h-4 ml-1" />
                      </Button>
                    </Link>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-2 mb-4">
                    <div className="text-center p-2 bg-red-50 rounded-lg">
                      <p className="text-xl font-bold text-red-600">{overview.tasks.counts.overdue}</p>
                      <p className="text-xs text-slate-500">Overdue</p>
                    </div>
                    <div className="text-center p-2 bg-amber-50 rounded-lg">
                      <p className="text-xl font-bold text-amber-600">{overview.tasks.counts.due_today}</p>
                      <p className="text-xs text-slate-500">Due Today</p>
                    </div>
                    <div className="text-center p-2 bg-slate-50 rounded-lg">
                      <p className="text-xl font-bold text-slate-700">{overview.tasks.counts.due_this_week}</p>
                      <p className="text-xs text-slate-500">This Week</p>
                    </div>
                    <div className="text-center p-2 bg-orange-50 rounded-lg">
                      <p className="text-xl font-bold text-orange-600">{overview.tasks.counts.escalated}</p>
                      <p className="text-xs text-slate-500">Escalated</p>
                    </div>
                  </div>
                  {overview.tasks.top.length > 0 ? (
                    <div className="space-y-2">
                      <p className="text-xs font-medium text-slate-500 uppercase">Priority Tasks</p>
                      {overview.tasks.top.map((t) => (
                        <Link key={t.id} to={`/ats/manual-selections/tasks`} className="block">
                          <div className="flex items-center justify-between p-2 hover:bg-slate-50 rounded-lg transition-colors">
                            <div className="min-w-0 flex-1">
                              <p className="text-sm text-slate-700 truncate">{t.title}</p>
                            </div>
                            <div className="flex items-center gap-1 flex-shrink-0 ml-2">
                              {t.is_overdue && <Badge className="bg-red-100 text-red-700 text-xs">Overdue</Badge>}
                              {!t.is_overdue && t.priority === 'urgent' && <Badge className="bg-red-100 text-red-700 text-xs">Urgent</Badge>}
                              {!t.is_overdue && t.priority === 'high' && <Badge className="bg-orange-100 text-orange-700 text-xs">High</Badge>}
                            </div>
                          </div>
                        </Link>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-slate-400 text-center py-4">No tasks pending</p>
                  )}
                </CardContent>
              </Card>

              {/* Client Emails Card */}
              <Card className="bg-white border-teal-200 hover:shadow-lg transition-all" data-testid="client-emails-card">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-10 h-10 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-lg flex items-center justify-center">
                        <MailOpen className="w-5 h-5 text-white" />
                      </div>
                      <CardTitle className="text-lg">Client Emails</CardTitle>
                    </div>
                    <Link to="/ats/manual-selections/client-emails">
                      <Button variant="ghost" size="sm" className="text-teal-600">
                        View <ChevronRight className="w-4 h-4 ml-1" />
                      </Button>
                    </Link>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-2 mb-4">
                    <div className="text-center p-2 bg-amber-50 rounded-lg">
                      <p className="text-xl font-bold text-amber-600">{overview.client_emails.counts.awaiting_24h}</p>
                      <p className="text-xs text-slate-500">&gt;24h Wait</p>
                    </div>
                    <div className="text-center p-2 bg-red-50 rounded-lg">
                      <p className="text-xl font-bold text-red-600">{overview.client_emails.counts.awaiting_72h}</p>
                      <p className="text-xs text-slate-500">&gt;72h Wait</p>
                    </div>
                    <div className="text-center p-2 bg-purple-50 rounded-lg">
                      <p className="text-xl font-bold text-purple-600">{overview.client_emails.counts.opened_not_replied}</p>
                      <p className="text-xs text-slate-500">Opened</p>
                    </div>
                    <div className="text-center p-2 bg-orange-50 rounded-lg">
                      <p className="text-xl font-bold text-orange-600">{overview.client_emails.counts.follow_ups_due}</p>
                      <p className="text-xs text-slate-500">Follow-up</p>
                    </div>
                  </div>
                  {overview.client_emails.awaiting.length > 0 ? (
                    <div className="space-y-2">
                      <p className="text-xs font-medium text-slate-500 uppercase">Awaiting Reply</p>
                      {overview.client_emails.awaiting.map((e) => (
                        <Link key={e.id} to={`/ats/manual-selections/client-emails`} className="block">
                          <div className="flex items-center justify-between p-2 hover:bg-slate-50 rounded-lg transition-colors">
                            <div className="min-w-0 flex-1">
                              <p className="text-sm text-slate-700 truncate">{e.subject}</p>
                              <p className="text-xs text-slate-500">{e.client_name}</p>
                            </div>
                            <div className="flex items-center gap-1 flex-shrink-0 ml-2">
                              {e.open_count > 0 && (
                                <Badge variant="outline" className="text-xs">
                                  <Eye className="w-3 h-3 mr-1" />{e.open_count}
                                </Badge>
                              )}
                              <span className="text-xs text-slate-500">{formatRelativeTime(e.sent_at)}</span>
                            </div>
                          </div>
                        </Link>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-slate-400 text-center py-4">No emails awaiting reply</p>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Permissions Info */}
            <div className="p-4 bg-slate-100 rounded-lg">
              <div className="flex items-start gap-3">
                <Users className="w-5 h-5 text-slate-500 mt-0.5" />
                <div className="text-sm text-slate-600">
                  <p className="font-medium text-slate-700 mb-1">
                    Viewing: {scope === 'all' ? 'All Team Data' : 'My Data Only'}
                  </p>
                  {isAdmin ? (
                    <p>As an admin, you can toggle between your items and all team items using the switch above.</p>
                  ) : (
                    <p>You can see items where you are the creator or assignee.</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        ) : null}
      </main>
    </div>
  );
}
